import {isReactCodeViewHeaderEnabled} from '@github-ui/code-view-header/is-enabled'
import {isReactCodeViewSidebarEnabled} from '@github-ui/code-view-sidebar/is-enabled'
import type {DataRouterFileOverviewPayload} from '@github-ui/code-view-types'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {RailsPartial, RailsPartialHTML} from '@github-ui/rails-partial'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ReposOverview} from '@github-ui/repos-overview'
import {SplitPageLayout} from '@primer/react'

import {AskCopilotNoReadmeCard} from '../components/ask-copilot/AskCopilotNoReadmeCard'
import {AskCopilotReadmeMenu} from '../components/ask-copilot/AskCopilotReadmeMenu'
import type {MergedHeaderPayload} from '../components/overview/OverviewHeader'
import {OverviewHeader} from '../components/overview/OverviewHeader'
import {OverviewSidebar} from '../components/overview/OverviewSidebar'
import {useUpdateRefInfoAndPath} from '../hooks/use-update-ref-info-and-path'
import {codeViewRepoRoute} from '../routes/code-view-repo-route'

export function ReposOverviewPage({
  payload,
  isTree,
  sidebarPayload,
}: {
  payload: DataRouterFileOverviewPayload
  isTree: boolean
  sidebarPayload: MergedHeaderPayload
}) {
  useUpdateRefInfoAndPath(payload.refInfo, payload.path)
  const reactSidebarEnabled = isReactCodeViewSidebarEnabled()
  const reactHeaderEnabled = isReactCodeViewHeaderEnabled()
  const askCopilotEnabled = isFeatureEnabled('repo_overview_ask_copilot') && (payload.copilotAccessAllowed ?? false)

  return (
    <SplitPageLayout className="container-xl">
      <SplitPageLayout.Header padding="none" divider="none" className="tmp-px-3 tmp-px-lg-5">
        {reactHeaderEnabled ? (
          <OverviewHeader overview={payload.overview} headerData={sidebarPayload} />
        ) : isTree ? (
          <RailsPartialHTML html={payload.overview.partials.header} partialName="codeViewTreeRoute.Header" />
        ) : (
          <RailsPartial route={codeViewRepoRoute} partialName="Header" />
        )}
      </SplitPageLayout.Header>
      <SplitPageLayout.Content as="div" padding="condensed">
        <ReposOverview
          payload={payload}
          className="tmp-pl-lg-3 mt-0"
          tree={payload.tree}
          readmeHeaderSlot={
            askCopilotEnabled && (
              <ErrorBoundary boundaryName="ask-copilot-readme-header" fallback={null}>
                <AskCopilotReadmeMenu />
              </ErrorBoundary>
            )
          }
          noReadmeSlot={
            askCopilotEnabled && (
              <ErrorBoundary boundaryName="ask-copilot-no-readme-card" fallback={null}>
                <AskCopilotNoReadmeCard />
              </ErrorBoundary>
            )
          }
        />
      </SplitPageLayout.Content>
      <SplitPageLayout.Pane position="end" sticky={false} divider="none" width="large" className="pr-2">
        {reactSidebarEnabled ? (
          <OverviewSidebar overview={payload.overview} sidebarPayload={sidebarPayload} />
        ) : isTree ? (
          <RailsPartialHTML html={payload.overview.partials.sidebar} partialName="codeViewTreeRoute.Sidebar" />
        ) : (
          <RailsPartial route={codeViewRepoRoute} partialName="Sidebar" />
        )}
      </SplitPageLayout.Pane>
    </SplitPageLayout>
  )
}
